// Блоки «месяц и объект» карточками: то же, что в таблице, для окна, в которое таблица не помещается.
//
// ## Зачем они
//
// Таблица из пятнадцати колонок на планшете в портрете (744 точки) была шире окна на 450 точек, и
// «Официально», «Форма», «Всего к выплате», «Выплачено» и «Осталось» уходили за край без единой
// подсказки о прокрутке (замечание независимой проверки 01.10.2026). Карточка не прячет ничего:
// сверху месяц, объект и состояние ведомости, под ними пары «подпись и число» в две колонки,
// словами под ними замечания, которые целиком переносятся по строкам.
//
// Порядок пар тот же, что у колонок таблицы: часы, тариф, затем деньги слева направо. Привычный
// по образцу заказчика столбец «сколько всего выдали авансом» тут читается сверху вниз по карточкам
// и подытожен последней карточкой «Всего»: так итог не приходится складывать в уме.
//
// ## Те же правила, что у таблицы
//
// Ноль по расчёту стоит числом и приглушён, расчёт не проводился — прочерк, часы не проставлены —
// прочерк и «табель не сдан»: это три разных факта, и их читают одинаково и в таблице, и здесь.
// Состав пар тот же, что у колонок: обучение, штраф, премия и прочие удержания появляются, только
// если у кого-то из блоков они не нулевые.

import type { ReactNode } from 'react';

import { Link } from 'react-router';

import type { PayslipPeriodBlock, PayslipPeriodTotals } from '@/shared/api/endpoints/payslip';
import { formatPeriodTitle } from '@/shared/time/period';
import { cn } from '@/utils/cn';

import {
  Деньги,
  СостояниеВедомости,
  видимыеКолонки,
  замечанияБлока,
  числоИзвестно,
  частиТарифа,
  частиЧасов,
} from './ТаблицаБлоков';
import { адресВедомости, итогНеизвестен, непосчитанноеВБлоках, часыТекстом } from './тексты';

/** Пара «подпись и число». Подпись слева, число у правого края своей колонки. */
function Пара({
  подпись,
  главная = false,
  children,
}: {
  подпись: string;
  /** Итог блока: выделена, она и есть ответ карточки. */
  главная?: boolean;
  children: ReactNode;
}) {
  return (
    <div className='flex items-baseline justify-between gap-3'>
      <dt
        className={cn(
          главная ? 'text-label-sm text-text-strong-950' : 'text-paragraph-xs text-text-sub-600',
        )}
      >
        {подпись}
      </dt>
      <dd className='min-w-0 whitespace-nowrap text-right tabular-nums text-paragraph-sm'>
        {children}
      </dd>
    </div>
  );
}

function ПараЧасов({ блок }: { блок: PayslipPeriodBlock }) {
  const { часы, норма } = частиЧасов(блок);
  const сданы = блок.hours !== null && блок.hours !== undefined;
  return (
    <Пара подпись='Часы'>
      <span className={сданы ? 'text-text-strong-950' : 'text-text-sub-600'}>{часы}</span>
      {/* Пробел между частями настоящий, а не отступ: «60 из 372» при копировании и при чтении
          вслух не должно слипаться в «60из 372». */}
      {сданы && норма !== null && (
        <>
          {' '}
          <span className='text-paragraph-xs text-text-sub-600'>{норма}</span>
        </>
      )}
      {!сданы && (
        <>
          {' '}
          <span className='text-paragraph-xs text-text-sub-600'>табель не сдан</span>
        </>
      )}
    </Пара>
  );
}

function ПараТарифа({ блок }: { блок: PayslipPeriodBlock }) {
  const { тариф, расчёт } = частиТарифа(блок);
  const известен = блок.rate !== null && блок.rate !== undefined;
  return (
    <Пара подпись='Тариф'>
      <span className={известен ? 'text-text-strong-950' : 'text-text-sub-600'}>{тариф}</span>
      {расчёт !== null && (
        <>
          {' '}
          <span className='text-paragraph-xs text-text-sub-600'>· {расчёт}</span>
        </>
      )}
    </Пара>
  );
}

export function КарточкиБлоков({
  блоки,
  итог,
  подпись,
  часыУжеНазваны,
}: {
  блоки: readonly PayslipPeriodBlock[];
  итог: PayslipPeriodTotals;
  /** Для чтения с экрана: какому юрлицу принадлежит список. */
  подпись: string;
  /** Месяцы и объекты, по которым над блоками уже сказано «табель не сдан». */
  часыУжеНазваны?: ReadonlySet<string>;
}) {
  const колонки = видимыеКолонки(блоки);
  // Ни по одной ведомости расчёта не было: «Всего» не нули, а неизвестно.
  const неизвестен = итогНеизвестен(непосчитанноеВБлоках(блоки), итог);
  return (
    <ul aria-label={подпись} className='flex flex-col'>
      {блоки.map((блок) => {
        const примечания = замечанияБлока(блок, часыУжеНазваны);
        return (
          <li
            key={блок.payroll_id}
            className='flex break-inside-avoid flex-col gap-2 border-b border-stroke-soft-200 px-4 py-3'
          >
            <div className='flex flex-wrap items-start justify-between gap-x-3 gap-y-1'>
              <div className='flex min-w-0 flex-col gap-0.5'>
                <Link
                  to={адресВедомости(блок.period_year, блок.period_month, блок.payroll_id)}
                  className='цель-тача relative w-fit text-label-sm text-text-strong-950 underline-offset-4 hover:underline'
                >
                  {formatPeriodTitle({ year: блок.period_year, month: блок.period_month })}
                </Link>
                <span className='break-words text-paragraph-xs text-text-sub-600'>
                  {блок.site_name ?? 'Аппарат управления'}
                </span>
              </div>
              <СостояниеВедомости блок={блок} />
            </div>
            <dl className='grid grid-cols-2 gap-x-6 gap-y-1.5'>
              <ПараЧасов блок={блок} />
              <ПараТарифа блок={блок} />
              {колонки.map((колонка) => (
                <Пара
                  key={колонка.ключ}
                  подпись={колонка.заголовок}
                  главная={колонка.главная === true}
                >
                  <Деньги
                    копейки={колонка.значение(блок)}
                    известно={числоИзвестно(блок, колонка)}
                    главная={колонка.главная === true}
                  />
                </Пара>
              ))}
            </dl>
            {примечания.length > 0 && (
              <ul className='flex flex-col gap-0.5'>
                {примечания.map((строка) => (
                  <li key={строка} className='text-paragraph-xs text-text-sub-600'>
                    {строка}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
      <li className='flex break-inside-avoid flex-col gap-2 bg-bg-weak-50 px-4 py-3'>
        <div className='flex items-baseline justify-between gap-3'>
          <span className='text-label-sm text-text-strong-950'>Всего</span>
          <span className='tabular-nums text-paragraph-sm text-text-strong-950'>
            {итог.hours === null || итог.hours === undefined
              ? '—'
              : `${часыТекстом(итог.hours)}\u00a0ч`}
          </span>
        </div>
        <dl className='grid grid-cols-2 gap-x-6 gap-y-1.5'>
          {колонки.map((колонка) => (
            <Пара key={колонка.ключ} подпись={колонка.заголовок} главная={колонка.главная === true}>
              <Деньги
                копейки={колонка.итог(итог)}
                известно={!неизвестен || колонка.всегдаЧисло === true}
                главная={колонка.главная === true}
              />
            </Пара>
          ))}
        </dl>
      </li>
    </ul>
  );
}
